import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import { toast } from "sonner";
import { Megaphone, Loader2, Send, ExternalLink } from "lucide-react";

/**
 * Admin → Notifications.
 * Compose a broadcast that fans out to every non-banned user as an in-app
 * bell notification (and a browser push if they've opted in). Recent
 * broadcasts are listed below for audit.
 *
 * Backend:
 *   POST /admin/broadcasts {title, body, link?, push?: bool}
 *   GET  /admin/broadcasts
 */
export default function BroadcastsTab() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [push, setPush] = useState(true);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/broadcasts");
      setHistory(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
      setHistory([]);
    }
  };
  useEffect(() => { load(); }, []);

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    if (!window.confirm("Send this broadcast to ALL users? This cannot be undone.")) return;
    setBusy(true);
    try {
      const payload = { title: title.trim(), body: body.trim(), push };
      if (link.trim()) payload.link = link.trim();
      const { data } = await api.post("/admin/broadcasts", payload);
      toast.success(`Sent to ${data.recipient_count} users`);
      setTitle(""); setBody(""); setLink("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <SectionTitle kicker="// admin control" testId="admin-broadcasts-title">Notifications</SectionTitle>

      <Panel className="space-y-4" data-testid="admin-broadcast-form">
        <div className="flex items-center gap-2">
          <Megaphone className="w-4 h-4 text-[#7C3AED]" />
          <h3 className="font-heading text-lg uppercase">Compose broadcast</h3>
        </div>
        <div className="space-y-2">
          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-1">Title</div>
            <input
              type="text"
              value={title}
              maxLength={120}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="admin-broadcast-title"
              placeholder="Short headline that appears in the bell"
              className="w-full px-3 py-2.5 bg-[#0F0F18] border border-[#2A2A30] text-white font-mono text-sm"
            />
            <div className="text-[10px] text-[#52525B] font-mono mt-0.5 text-right">{title.length}/120</div>
          </label>
          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-1">Body</div>
            <textarea
              value={body}
              maxLength={1000}
              onChange={(e) => setBody(e.target.value)}
              rows={4}
              data-testid="admin-broadcast-body"
              placeholder="What's the news?"
              className="w-full px-3 py-2.5 bg-[#0F0F18] border border-[#2A2A30] text-white font-mono text-sm resize-y"
            />
            <div className="text-[10px] text-[#52525B] font-mono mt-0.5 text-right">{body.length}/1000</div>
          </label>
          <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-1">Link (optional)</div>
            <input
              type="text"
              value={link}
              maxLength={300}
              onChange={(e) => setLink(e.target.value)}
              data-testid="admin-broadcast-link"
              placeholder="/v2/world-cup/mobile (where the notification opens)"
              className="w-full px-3 py-2.5 bg-[#0F0F18] border border-[#2A2A30] text-white font-mono text-sm"
            />
          </label>
          <label className="inline-flex items-center gap-2 mt-2 cursor-pointer" data-testid="admin-broadcast-push-label">
            <input
              type="checkbox"
              checked={push}
              onChange={(e) => setPush(e.target.checked)}
              data-testid="admin-broadcast-push"
              className="w-4 h-4 accent-[#7C3AED]"
            />
            <span className="text-xs text-[#A1A1AA]">Also send browser push (to users who opted in)</span>
          </label>
        </div>
        <button
          type="button"
          onClick={send}
          disabled={busy || !title.trim() || !body.trim()}
          data-testid="admin-broadcast-send"
          className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white font-bold text-sm rounded-lg shadow-lg shadow-[#7C3AED]/30 hover:opacity-95 disabled:opacity-50 transition-opacity"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Send to everyone
        </button>
      </Panel>

      <Panel className="space-y-3" data-testid="admin-broadcast-history">
        <h3 className="font-heading text-lg uppercase">Recent broadcasts</h3>
        {history === null ? <TactLoader /> :
         history.length === 0 ? (
           <div className="font-mono text-sm text-[#71717A]">[ NO BROADCASTS SENT YET ]</div>
         ) : (
          <ul className="divide-y divide-[#2A2A30]">
            {history.map((b) => (
              <li key={b.id} className="py-3" data-testid={`admin-broadcast-row-${b.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-white truncate">{b.title}</div>
                    <div className="text-xs text-[#A1A1AA] mt-0.5 whitespace-pre-wrap break-words">{b.body}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B] mt-1.5 flex items-center gap-2">
                      <span>{new Date(b.created_at).toLocaleString()}</span>
                      <span>·</span>
                      <span>by {b.sent_by}</span>
                      <span>·</span>
                      <span>{b.recipient_count} recipients</span>
                      {b.push && <><span>·</span><span className="text-[#7C3AED]">PUSH</span></>}
                      {b.link && (
                        <a href={b.link} className="text-[#A5B4FC] inline-flex items-center gap-0.5 hover:underline">
                          <ExternalLink className="w-2.5 h-2.5" /> link
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
         )}
      </Panel>
    </div>
  );
}
